/* istanbul ignore file */
import { createAction } from '@reduxjs/toolkit';
import type { IApplicationLocation, IRelationshipManager } from '../../api/domain/application';
import type { INotification, INotificationClientAdvisory } from '../../api/domain/channelsuite/notifications/notification';
import type { IVault } from '../../api/domain/channelsuite/secure-document-store';
import type { IUserMailbox } from '../../api/domain/channelsuite/secure-inbox';
import type { ICustomerBase } from '../../api/domain/customer-base';
import type { Tenant } from '../../enum/tenant';
import type { ProductCardType } from '../../enum/product-card-type';

enum ApplicationActions {
    INITIALIZE_APPLICATION = 'application.initialize',
    APPLICATION_INITIALIZED = 'application.initialize.success',
    CHANGE_TENANT = 'application.tenant.change',
    LOGIN = 'application.login', // for development
    STRONG_LOGIN = 'application.login.strong', // for development
    LOGOUT = 'application.logout', // for development
    ADD_NOTIFICATION = 'application.add-notification',
    ADD_NOTIFICATION_CLIENT_ADVISORY = 'application.add-notification-client-advisory',
    ADD_NOTIFICATION_SUCCESS = 'application.add-notification.success',
    CLEAR_NOTIFICATIONS = 'application.clear-notifications',
    SET_USER_IS_PROSPECT = 'application.set-user-is-prospect',
    SHOW_PROGRESS_INDICATOR = 'application.show-progress-indicator',
    HIDE_PROGRESS_INDICATOR = 'application.hide-progress-indicator',
    CHANGE_LOCATION = 'application.change-location',
    CHANGE_LOCATION_SUCCESS = 'application.tenant.change-location.success',
    CHANGE_LOCATION_FAILURE = 'application.tenant.change-location.failure',
    DISABLE_SCROLLING = 'application.disable-scrolling',
    ENABLE_SCROLLING = 'application.enable-scrolling',
    HIDE_STICKY_NAVIGATION_ON_MOBILE = 'application.hide-sticky-navigation-on-mobile',
    SHOW_STICKY_NAVIGATION_ON_MOBILE = 'application.show-sticky-navigation-on-mobile',
    SELECT_CARD_TYPE = 'application.select-card-type',
    SELECT_CUSTOMER_BASE = 'application.select-customer-base',
    SELECT_CUSTOMER_BASE_FOR_PRODUCTS = 'application.select-customer-base-for-products',
    SELECT_CUSTOMER_BASE_FOR_ESERVICES = 'application.select-customer-base-for-e-services',
    SELECT_MAILBOXES = 'application.select-mailboxes',
    SELECT_VAULT = 'application.select-vault',
    SET_USER_RELATIONSHIP_MANAGER = 'application.set.relationship-manager',
    SET_CONSENT_MATOMO = 'application.set.consent.matomo',
    SET_CONSENT_MATOMO_FAILURE = 'application.set.consent.matomo.failure',
    SET_CONSENT_MATOMO_SUCCESS = 'application.set.consent.matomo.success',
}

export const setRelationshipManager = createAction<IRelationshipManager | undefined>(ApplicationActions.SET_USER_RELATIONSHIP_MANAGER);
export const selectCardType = createAction<ProductCardType>(ApplicationActions.SELECT_CARD_TYPE);
export const selectCustomerBase = createAction<ICustomerBase | undefined>(ApplicationActions.SELECT_CUSTOMER_BASE);
export const selectCustomerBaseForProducts = createAction<ICustomerBase | undefined>(ApplicationActions.SELECT_CUSTOMER_BASE_FOR_PRODUCTS);
export const selectCustomerBaseForEServices = createAction<ICustomerBase | undefined>(ApplicationActions.SELECT_CUSTOMER_BASE_FOR_ESERVICES);
export const selectMailboxes = createAction<IUserMailbox[]>(ApplicationActions.SELECT_MAILBOXES);
export const selectVault = createAction<IVault | undefined>(ApplicationActions.SELECT_VAULT);
export const addNotificationSuccess = createAction<INotification>(ApplicationActions.ADD_NOTIFICATION_SUCCESS);
export const addNotification = createAction<INotification>(ApplicationActions.ADD_NOTIFICATION);
export const addNotificationClientAdvisory = createAction<INotificationClientAdvisory | undefined>(ApplicationActions.ADD_NOTIFICATION_CLIENT_ADVISORY);
export const clearNotifications = createAction(ApplicationActions.CLEAR_NOTIFICATIONS);
export const setUserIsProspect = createAction(ApplicationActions.SET_USER_IS_PROSPECT);
export const changeTenant = createAction<Tenant>(ApplicationActions.CHANGE_TENANT);
export const initializeApplication = createAction(ApplicationActions.INITIALIZE_APPLICATION);
export const applicationInitialized = createAction(ApplicationActions.APPLICATION_INITIALIZED);
export const login = createAction(ApplicationActions.LOGIN);
export const strongLogin = createAction(ApplicationActions.STRONG_LOGIN);
export const logout = createAction(ApplicationActions.LOGOUT);
export const showProgressIndicator = createAction(ApplicationActions.SHOW_PROGRESS_INDICATOR);
export const hideProgressIndicator = createAction(ApplicationActions.HIDE_PROGRESS_INDICATOR);
export const changeLocation = createAction<IApplicationLocation<any, any>>(ApplicationActions.CHANGE_LOCATION);
export const changeLocationSuccess = createAction(ApplicationActions.CHANGE_LOCATION_SUCCESS);
export const changeLocationFailure = createAction(ApplicationActions.CHANGE_LOCATION_FAILURE);
export const disableScrolling = createAction(ApplicationActions.DISABLE_SCROLLING);
export const enableScrolling = createAction(ApplicationActions.ENABLE_SCROLLING);
export const showStickyNavigationOnMobile = createAction(ApplicationActions.SHOW_STICKY_NAVIGATION_ON_MOBILE);
export const hideStickyNavigationOnMobile = createAction(ApplicationActions.HIDE_STICKY_NAVIGATION_ON_MOBILE);
export const setConsentMatomo = createAction<{ allowTracking: boolean }>(ApplicationActions.SET_CONSENT_MATOMO);
export const setConsentMatomoFailure = createAction(ApplicationActions.SET_CONSENT_MATOMO_FAILURE);
export const setConsentMatomoSuccess = createAction(ApplicationActions.SET_CONSENT_MATOMO_SUCCESS);
